import { decodeUtf8Mojibake } from "../../../utils/filenameEncoding";

const displayName = (value) => decodeUtf8Mojibake(value);

const cleanExtension = (ext) => {
  if (typeof ext !== "string") return ext;
  return ext.startsWith(".") ? ext.slice(1) : ext;
};

export const mapTextItem = (t, i) => ({
  id: `text-${t.upload_id || t.id || i}`,
  backendId: String(t.upload_id || t.id || i),
  itemSource: "user_upload",
  userUploadType: "text",
  pythonFileUuid: t.upload_id,
  name:
    displayName(t.text_title) ||
    displayName(t.name) ||
    displayName(t.file_name) ||
    (t.text_content ? t.text_content.slice(0, 60) : `Text ${i + 1}`),
  type: "file",
  parentId: null,
  size: t.size || t.characters || 0,
  updatedAt: t.updated_timestamp || t.updatedAt || t.created_timestamp,
  uploadedBy: t.submitted_by || t.uploaded_by || "Me",
  isPersonalSpace: false,
  isUnorganized: false,
  approvalStatus: t.approval_status || "approved",
  fileStatus: t.upload_status || "1",
  createdAt: t.created_timestamp || t.createdAt,
  fileType: "text",
  fileExtension: "txt",
  ownerType: "admin",
  mode: t.mode || "global",
});

export const mapQaItem = (q, i) => ({
  id: `qa-${q.upload_id || q.id || i}`,
  backendId: String(q.upload_id || q.id || i),
  itemSource: "user_upload",
  userUploadType: "qa",
  pythonFileUuid: q.upload_id,
  name: q.question
    ? `${q.question.slice(0, 80)}${q.question.length > 80 ? "..." : ""}`
    : `Q&A ${i + 1}`,
  type: "file",
  parentId: null,
  size: (q.question?.length || 0) + (q.answer?.length || 0),
  updatedAt: q.updated_timestamp || q.updatedAt || q.created_timestamp,
  uploadedBy: q.submitted_by || q.uploaded_by || "Me",
  isPersonalSpace: false,
  isUnorganized: false,
  approvalStatus: q.upload_status === "1" ? "approved" : "pending",
  fileStatus: q.upload_status || "1",
  createdAt: q.created_timestamp || q.createdAt,
  fileType: "qa",
  fileExtension: "qa",
  ownerType: "admin",
  mode: q.mode || "global",
});

export const mapTrainedItem = (item) => ({
  id: String(item.id),
  backendId: String(
    item.itemType === "uploaded"
      ? item.uploaded_file_id || item.id
      : item.id,
  ),
  itemSource:
    item.itemType === "uploaded" ? "uploaded_file" : "trained_document",
  name: displayName(item.name),
  type: item.itemType === "folder" ? "folder" : "file",
  parentId: item.parentId ? String(item.parentId) : null,
  size: item.size,
  updatedAt: item.updatedAt,
  uploadedBy: item.uploadedBy,
  uploaded_file_id: item.uploaded_file_id,
  pythonFileUuid: item.pythonFileUuid,
  document_id: item.document_id ?? null,
  departmentId: item.department_fid ? String(item.department_fid) : null,
  groupId: item.department_fid ? String(item.department_fid) : item.groupId || null,
  mode: item.department_fid ? "group" : item.mode,
  ownerType: item.department_fid ? "admin" : item.ownerType || "user",
  isUnorganized: false,
  approvalStatus: item.status || "approved",
  fileStatus: item.fileStatus || "1",
  createdAt: item.createdAt || item.updatedAt,
  fileType: item.type,
  fileExtension: cleanExtension(item.file_extension),
});

export const mapUnorganizedItem = (doc) => ({
  id: `unorganized-${doc.id || doc.file_id}`,
  backendId: String(doc.id || doc.file_id),
  itemSource: "uploaded_file",
  name: displayName(doc.name) || displayName(doc.original_filename),
  type: "file",
  parentId: doc.folder_id ? String(doc.folder_id) : null,
  size: doc.size || doc.file_size,
  updatedAt: doc.updatedAt || doc.created_timestamp,
  uploadedBy: doc.uploaded_by || "Me",
  uploaded_file_id: doc.id || doc.file_id,
  pythonFileUuid: doc.python_file_uuid,
  document_id: doc.document_id ?? null,
  departmentId: doc.department_fid ? String(doc.department_fid) : null,
  groupId: doc.department_fid ? String(doc.department_fid) : null,
  mode:
    doc.department_fid ? "group" : doc.mode || doc.upload_mode,
  ownerType:
    doc.department_fid ||
    doc.mode === "global" ||
    doc.mode === "group" ||
    doc.upload_mode === "global" ||
    doc.upload_mode === "group"
      ? "admin"
      : "user",
  isUnorganized: true,
  approvalStatus: doc.approval_status || "approved",
  fileStatus: doc.file_status || "1",
  createdAt: doc.createdAt,
  fileType: doc.type,
  fileExtension: cleanExtension(doc.file_extension),
});

export const mapPersonalSpaceFolder = (folder) => ({
  id: `ps-folder-${folder.user_files_id}`,
  backendId: String(folder.user_files_id),
  itemSource: "personal_space",
  name: displayName(folder.name) || displayName(folder.user_files_name),
  type: "folder",
  parentId: folder.parent_id ? `ps-folder-${folder.parent_id}` : null,
  size: 0,
  updatedAt: folder.updated_timestamp || folder.created_timestamp,
  uploadedBy: "Me",
  isPersonalSpace: true,
  isUnorganized: false,
  approvalStatus: "approved",
  fileStatus: folder.user_files_status || "1",
  createdAt: folder.created_timestamp,
  fileType: "folder",
  fileExtension: "folder",
  originalId: folder.user_files_id,
  ownerType: "user",
  mode: "personal_space",
});

export const mapPersonalSpaceFile = (doc) => ({
  id: `ps-file-${doc.user_files_id}`,
  backendId: String(doc.user_files_id),
  itemSource: "personal_space",
  name: displayName(doc.name) || displayName(doc.user_files_name),
  type: "file",
  parentId: doc.parent_id ? `ps-folder-${doc.parent_id}` : null,
  size: doc.file_size,
  updatedAt: doc.updated_timestamp || doc.created_timestamp,
  uploadedBy: "Me",
  isPersonalSpace: true,
  isUnorganized: false,
  approvalStatus: "approved",
  fileStatus: doc.user_files_status || "1",
  createdAt: doc.created_timestamp,
  fileType: doc.file_type || cleanExtension(doc.file_extension),
  fileExtension: cleanExtension(doc.file_extension),
  originalId: doc.user_files_id,
  document_id: doc.document_id ?? null,
  ownerType: "user",
  mode: "personal_space",
});

/**
 * Map a raw enterprise group (department) object into a root-level folder
 * for the Group Repository view.
 */
export const mapGroupFolder = (group) => ({
  id: `group-${group.id}`,
  backendId: String(group.id),
  itemSource: "group",
  name: group.name,
  type: "folder",
  parentId: null,
  size: 0,
  updatedAt: group.updatedAt || new Date().toISOString(),
  uploadedBy: "System",
  isPersonalSpace: false,
  isUnorganized: false,
  approvalStatus: "approved",
  fileStatus: "1",
  createdAt: group.createdAt || new Date().toISOString(),
  fileType: "folder",
  fileExtension: "folder",
  ownerType: "admin",
  mode: "group",
  groupId: String(group.id),
  // Group root folders cannot be deleted, renamed, or moved.
  isGroupRoot: true,
});

/**
 * Map a trained-document item that belongs to a group.
 * Stamps groupId and mode:"group" onto the standard mapped item,
 * and sets parentId to the group root folder when the item has no parent.
 */
export const mapGroupItem = (item, groupId) => {
  const base = mapTrainedItem(item);
  // If an item has no parentId (it's at the top level of the group),
  // nest it under the group root folder.
  const parentId = base.parentId ?? `group-${groupId}`;
  return {
    ...base,
    parentId,
    mode: "group",
    groupId: String(groupId),
    ownerType: "admin",
  };
};
